import './intro-section.scss';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';

gsap.registerPlugin( Draggable, InertiaPlugin );

/**
 * GSAP horizontal loop — seamless infinite carousel.
 * Adapted from the GSAP docs utility.
 *
 * @param {HTMLElement[]} items  Elements to loop
 * @param {Object}        config Options (speed, paused, paddingRight, reversed, snap)
 * @return {gsap.core.Timeline}
 */
function horizontalLoop( items, config ) {
	items = gsap.utils.toArray( items );
	config = config || {};

	const tl = gsap.timeline( {
		repeat: -1,
		paused: config.paused,
		defaults: { ease: 'none' },
		onReverseComplete: () => tl.totalTime( tl.rawTime() + tl.duration() * 100 ),
	} );

	const length = items.length;
	const startX = items[ 0 ].offsetLeft;
	const times = [];
	const widths = [];
	const xPercents = [];
	let curIndex = 0;
	const pixelsPerSecond = ( config.speed || 1 ) * 100;
	const snap = config.snap === false ? ( v ) => v : gsap.utils.snap( config.snap || 1 );

	gsap.set( items, { xPercent: 0 } );

	for ( let i = 0; i < length; i++ ) {
		widths[ i ] = parseFloat( gsap.getProperty( items[ i ], 'width', 'px' ) );
		xPercents[ i ] = snap(
			( parseFloat( gsap.getProperty( items[ i ], 'x', 'px' ) ) / widths[ i ] ) * 100 +
			gsap.getProperty( items[ i ], 'xPercent' )
		);
	}

	gsap.set( items, { x: 0 } );

	const totalWidth =
		items[ length - 1 ].offsetLeft +
		( xPercents[ length - 1 ] / 100 ) * widths[ length - 1 ] -
		startX +
		items[ length - 1 ].offsetWidth * gsap.getProperty( items[ length - 1 ], 'scaleX' ) +
		( parseFloat( config.paddingRight ) || 0 );

	for ( let i = 0; i < length; i++ ) {
		const curX = ( xPercents[ i ] / 100 ) * widths[ i ];
		const distanceToStart = items[ i ].offsetLeft + curX - startX;
		const distanceToLoop = distanceToStart + widths[ i ] * gsap.getProperty( items[ i ], 'scaleX' );

		tl.to(
			items[ i ],
			{
				xPercent: snap( ( ( curX - distanceToLoop ) / widths[ i ] ) * 100 ),
				duration: distanceToLoop / pixelsPerSecond,
			},
			0
		).fromTo(
			items[ i ],
			{ xPercent: snap( ( ( curX - distanceToLoop + totalWidth ) / widths[ i ] ) * 100 ) },
			{
				xPercent: snap( ( curX / widths[ i ] ) * 100 ),
				duration: ( curX - distanceToLoop + totalWidth ) / pixelsPerSecond,
				immediateRender: false,
			},
			distanceToLoop / pixelsPerSecond
		).add( 'label' + i, distanceToStart / pixelsPerSecond );

		times[ i ] = distanceToStart / pixelsPerSecond;
	}

	function toIndex( index, vars ) {
		vars = vars || {};
		if ( Math.abs( index - curIndex ) > length / 2 ) {
			index += index > curIndex ? -length : length;
		}
		const newIndex = gsap.utils.wrap( 0, length, index );
		let time = times[ newIndex ];
		if ( ( time > tl.time() ) !== ( index > curIndex ) ) {
			vars.modifiers = { time: gsap.utils.wrap( 0, tl.duration() ) };
			time += tl.duration() * ( index > curIndex ? 1 : -1 );
		}
		curIndex = newIndex;
		vars.overwrite = true;
		return tl.animateTo( time, vars );
	}

	tl.next = ( vars ) => toIndex( curIndex + 1, vars );
	tl.prev = ( vars ) => toIndex( curIndex - 1, vars );
	tl.current = () => curIndex;
	tl.toIndex = ( index, vars ) => toIndex( index, vars );
	tl.times = times;
	tl.progress( 1, true ).progress( 0, true );

	if ( config.reversed ) {
		tl.vars.onReverseComplete();
		tl.reverse();
	}

	return tl;
}

/**
 * Intro section — draggable horizontal loop slider with floating badge.
 * Scoped to [data-component="intro-section"]; supports one instance per page.
 */
function initIntroSection() {
	const root = document.querySelector( '[data-component="intro-section"]' );

	if ( ! root ) {
		return;
	}

	const slider = root.querySelector( '[data-intro-slider]' );

	if ( ! slider ) {
		return;
	}

	const cards = gsap.utils.toArray( root.querySelectorAll( '.b-introSection__card' ) );
	let loop = null;
	let draggableInstance = null;
	let resizeTimer = null;

	function initSlider() {
		if ( draggableInstance ) {
			draggableInstance.kill();
		}
		if ( loop ) {
			loop.kill();
			gsap.set( cards, { clearProps: 'all' } );
		}

		const sliderGap = parseFloat( window.getComputedStyle( slider ).gap ) || 0;

		loop = horizontalLoop( cards, {
			speed: 1,
			paused: true,
			paddingRight: sliderGap,
		} );

		const proxy = document.createElement( 'div' );
		const totalWidth = loop.duration() * 100;

		const draggables = Draggable.create( proxy, {
			trigger: slider,
			type: 'x',
			inertia: true,
			dragResistance: 0,
			resistance: 3000,
			onDrag() {
				loop.progress( gsap.utils.wrap( 0, 1, -this.x / totalWidth ) );
			},
			onThrowUpdate() {
				loop.progress( gsap.utils.wrap( 0, 1, -this.x / totalWidth ) );
			},
			onDragStart() {
				loop.pause();
			},
		} );

		draggableInstance = draggables[ 0 ];
	}

	initSlider();

	function handleResize() {
		clearTimeout( resizeTimer );
		resizeTimer = setTimeout( () => {
			initSlider();
		}, 200 );
	}

	window.addEventListener( 'resize', handleResize );

	// Badge follower
	let mouseX = 0;
	let mouseY = 0;
	let activeContainer = null;
	let activeBadge = null;
	let activeXTo = null;
	let activeYTo = null;

	function onMouseMove( e ) {
		mouseX = e.clientX;
		mouseY = e.clientY;

		if ( draggableInstance && draggableInstance.isDragging ) {
			if ( activeContainer ) {
				activeContainer.dispatchEvent( new MouseEvent( 'mouseleave' ) );
			}
			return;
		}

		const el = document.elementFromPoint( mouseX, mouseY );

		if ( el ) {
			const targetContainer = el.closest( '[data-intro-image]' );

			if ( targetContainer && targetContainer !== activeContainer ) {
				if ( activeContainer ) {
					activeContainer.dispatchEvent( new MouseEvent( 'mouseleave' ) );
				}
				targetContainer.dispatchEvent( new MouseEvent( 'mouseenter' ) );
			} else if ( ! targetContainer && activeContainer ) {
				activeContainer.dispatchEvent( new MouseEvent( 'mouseleave' ) );
			}
		} else if ( activeContainer ) {
			activeContainer.dispatchEvent( new MouseEvent( 'mouseleave' ) );
		}

		if ( activeContainer && activeBadge && activeXTo && activeYTo ) {
			const rect = activeContainer.getBoundingClientRect();
			activeXTo( mouseX - rect.left - activeBadge.offsetWidth / 2 );
			activeYTo( mouseY - rect.top + 20 );
		}
	}

	window.addEventListener( 'mousemove', onMouseMove );

	const hoverCards = root.querySelectorAll( '.b-introSection__card' );

	hoverCards.forEach( ( card ) => {
		const imageContainer = card.querySelector( '[data-intro-image]' );
		const badge = card.querySelector( '[data-intro-badge]' );

		if ( ! imageContainer || ! badge ) {
			return;
		}

		gsap.set( badge, { x: 0, y: 0, autoAlpha: 0, clipPath: 'inset(0 50% 0 50%)' } );

		const xTo = gsap.quickTo( badge, 'x', { duration: 0.6, ease: 'power3.out' } );
		const yTo = gsap.quickTo( badge, 'y', { duration: 0.6, ease: 'power3.out' } );

		imageContainer.addEventListener( 'mouseenter', () => {
			gsap.to( root.querySelectorAll( '[data-intro-badge]' ), {
				autoAlpha: 0,
				clipPath: 'inset(0 50% 0 50%)',
				duration: 0.2,
				overwrite: 'auto',
			} );

			activeContainer = imageContainer;
			activeBadge = badge;
			activeXTo = xTo;
			activeYTo = yTo;

			const rect = imageContainer.getBoundingClientRect();
			gsap.set( badge, {
				x: mouseX - rect.left - badge.offsetWidth / 2,
				y: mouseY - rect.top + 20,
			} );
			gsap.to( badge, {
				autoAlpha: 1,
				clipPath: 'inset(0 0% 0 0%)',
				duration: 0.4,
				ease: 'power3.out',
				overwrite: 'auto',
			} );
		} );

		imageContainer.addEventListener( 'mouseleave', () => {
			if ( activeContainer === imageContainer ) {
				activeContainer = null;
				activeBadge = null;
				activeXTo = null;
				activeYTo = null;
			}
			gsap.to( badge, {
				autoAlpha: 0,
				clipPath: 'inset(0 50% 0 50%)',
				duration: 0.3,
				ease: 'power3.in',
				overwrite: 'auto',
			} );
		} );
	} );

	// Teardown registry
	root._introCleanup = () => {
		window.removeEventListener( 'resize', handleResize );
		window.removeEventListener( 'mousemove', onMouseMove );
		clearTimeout( resizeTimer );
		if ( draggableInstance ) {
			draggableInstance.kill();
		}
		if ( loop ) {
			loop.kill();
		}
	};
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initIntroSection );
} else {
	initIntroSection();
}
